import { usePipeline } from '../../context/PipelineContext';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import SectionLabel from '../common/SectionLabel';

const STAGE_COLORS = ['#9DD4D9', '#5FBAC2', '#2E9DA6', '#006D78', '#004D54'];

export default function PipelineSnapshot() {
  const { pipelineKPIs, stageSummary, prospects } = usePipeline();

  const {
    totalActive,
    qualifiedActive,
    qualificationRate,
    weightedValue,
    winRate,
    closedWonCount,
    closedLostCount,
    unqualifiedCount,
  } = pipelineKPIs;

  // Count by division
  const bwmActive = (prospects || []).filter(p => p.stage !== 'closed-won' && p.stage !== 'closed-lost' && p.stage !== 'unqualified' && (p.division === 'bwm' || (!p.division && ['guided-wealth', 'total-wealth'].includes(p.serviceTier)))).length;
  const btsActive = (prospects || []).filter(p => p.stage !== 'closed-won' && p.stage !== 'closed-lost' && p.stage !== 'unqualified' && (p.division === 'bts' || (!p.division && ['tax-individual', 'tax-business', 'business-package'].includes(p.serviceTier)))).length;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Pipeline</SectionLabel>

      {/* Weighted value - headline */}
      <div className="mt-2 mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-0.5">
          Weighted Pipeline
        </div>
        <div className="text-[28px] font-bold text-navy leading-tight">
          {formatCurrency(weightedValue)}
        </div>
      </div>

      {/* Mini stage funnel */}
      <div className="flex gap-0.5 h-4 mb-3 rounded-[3px] overflow-hidden">
        {stageSummary.map((stage, i) => {
          const totalCount = stageSummary.reduce((s, st) => s + st.count, 0);
          const width = totalCount > 0 ? (stage.count / totalCount) * 100 : 20;
          return (
            <div
              key={stage.stageId}
              style={{ width: `${Math.max(width, 3)}%`, backgroundColor: STAGE_COLORS[i] }}
              title={`${stage.label}: ${stage.count} prospects`}
            />
          );
        })}
      </div>

      {/* Key metrics */}
      <div className="space-y-2 text-[12px]">
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Active Prospects</span>
          <span className="font-bold text-navy">{totalActive}</span>
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Qualified Now</span>
          <span className="font-bold text-navy">{qualifiedActive}</span>
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">By Division</span>
          <div className="flex gap-2 text-[11px]">
            <span className="text-bullogic-blue font-bold">BWM {bwmActive}</span>
            <span className="text-deep-teal font-bold">BTS {btsActive}</span>
          </div>
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Qualification Rate</span>
          {qualificationRate != null ? (
            <span className="font-bold text-deep-teal">{formatPercent(qualificationRate)}</span>
          ) : (
            <span className="text-[#ccc]">--</span>
          )}
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Win Rate (qualified)</span>
          {winRate != null ? (
            <span className="font-bold text-deep-teal">{formatPercent(winRate)}</span>
          ) : (
            <span className="text-[#ccc]">--</span>
          )}
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Outcomes</span>
          <div className="flex gap-2 text-[11px]">
            <span className="text-deep-teal font-bold">{closedWonCount} Won</span>
            <span className="text-rose font-bold">{closedLostCount} Lost</span>
            <span className="text-[#888] font-bold">{unqualifiedCount} Unqual.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
